import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { WebOddsType } from '@nf/utils-common/constants';
import { VoucherConditionMode, VoucherSystemVersion } from '@nf/achievement-system-v2';
import { useBetslipStore } from '@/store/betslip';
import { hkOdds, indoOddsToMyOdds, usOddsToMyOdds } from '@/utils/odds-utils';
import { type VoucherWithSupportStatus } from '../contexts/voucher-context';

type TranslateGeneral = ReturnType<typeof useTranslations<'General'>>;

export type TicketForValidation = {
	SportType?: number;
	LeagueId?: number;
	Bettype?: string | number;
	DisplayOdds?: number | string;
	OddsType?: number;
	PriceType?: number;
	isSBR?: boolean;
};

type FreeBetOddsLimitRule = {
	OddsType: number;
	MinOdds: number | null;
	MaxOdds: number | null;
};

enum ValidationMode {
	ALLOW = 1,  // 支援 (list 內的允許)
	DENY = 2    // 不支援 (list 內的拒絕)
}

type FreeBetConditionForValidation = {
	OddsLimitRule: FreeBetOddsLimitRule[] | null;
	SportMode: number;
	Sports: number[];
	LeagueIdMode: number;
	LeagueIds: number[];
	BetTypeMode: number;
	BetTypes: number[];
};

type FreeBetSportConditionForValidation = {
	SportType: number;
	AllLeagues: boolean;
	LeagueIds: number[];
	AllBetTypes: boolean;
	BetTypesMode?: number;
	BetTypes: number[];
};

export type FreeBetVoucherConditionForValidation = {
	ConditionMode?: number;
	VoucherSystemVersion?: number;
	Condition: FreeBetConditionForValidation & { SportConditions?: FreeBetSportConditionForValidation[] };
};

export type MinMaxBetCheckResult = {
	isDisabledByMinMaxBet: boolean;
	disableMessage: string | null;
};

type MinMaxBetLimitsType = {
	minBet: number;
	maxBet: number;
};

export type FreeBetConditionValidationResult = {
	isEnabled: boolean;
	disableMessages: string[] | null;
	disableMessageData?: {
		minOdds?: number;
		maxOdds?: number;
		oddsType?: number;
	};
};


const isValueAllowedByMode = (mode: number, conditions: number[], value: number | undefined): boolean => {
	if (value == null) return false;
	
	if (conditions.length === 0) return true;
	
	const isInList = conditions.includes(value);
	
	if (mode === ValidationMode.ALLOW) {
		return isInList;
	} else if (mode === ValidationMode.DENY) {
		return !isInList;
	}
	
	return true;
};

export const validateFreeBetCondition = (
	condition: FreeBetVoucherConditionForValidation | undefined,
	ticket: TicketForValidation | undefined,
	baseOddsType: number
): FreeBetConditionValidationResult => {

	if (!condition?.Condition || !ticket) {
		return { isEnabled: true, disableMessages: null };
	}

	const parsedBetType = ticket.Bettype != null ? Number(ticket.Bettype) : undefined;
	const betType = parsedBetType == null || Number.isNaN(parsedBetType) ? undefined : parsedBetType;
	const isPromotionSuiteSpecificSports = condition.VoucherSystemVersion === VoucherSystemVersion.PromotionSuite
		&& condition.ConditionMode === VoucherConditionMode.SpecificSports;
	const disableMessages = isPromotionSuiteSpecificSports
		? validateSportConditionBySport(condition.Condition.SportConditions ?? [], ticket, betType)
		: validateSportCondition(condition.Condition, ticket, betType);

	const oddsValidation = validateFreeBetOddsByCondition(condition, ticket, baseOddsType);
	if (!oddsValidation.isEnabled && oddsValidation.disableMessages?.length) {
		disableMessages.push(...oddsValidation.disableMessages);
	}

	if (disableMessages.length === 0) {
		return { isEnabled: true, disableMessages: null };
	}

	return {
		isEnabled: false,
		disableMessages,
		...(oddsValidation.disableMessageData != null
			? { disableMessageData: oddsValidation.disableMessageData }
			: {}),
	};
};

const validateSportCondition = (
	condition: FreeBetConditionForValidation,
	ticket: TicketForValidation,
	betType: number | undefined
): string[] => {
	const { Sports, SportMode, LeagueIds, LeagueIdMode, BetTypes, BetTypeMode } = condition;
	const disableMessages: string[] = [];

	if (!isValueAllowedByMode(SportMode, Sports, ticket.SportType)) {
		disableMessages.push('SPORT');
	}

	if (!isValueAllowedByMode(LeagueIdMode, LeagueIds, ticket.LeagueId)) {
		disableMessages.push('LEAGUE');
	}

	if (!isValueAllowedByMode(BetTypeMode, BetTypes, betType)) {
		disableMessages.push('BET_TYPE');
	}

	return disableMessages;
};

const isSportBetTypeAllowed = (sportCondition: FreeBetSportConditionForValidation, betType: number | undefined): boolean => {
	if (sportCondition.BetTypesMode === ValidationMode.DENY) {
		return betType != null && !sportCondition.BetTypes.includes(betType);
	}

	return sportCondition.AllBetTypes || (betType != null && sportCondition.BetTypes.includes(betType));
};

// Promotion Suite 指定運動：AllLeagues / AllBetTypes 為 false 時只允許清單內的值，BetTypesMode 為 2 時排除清單內的值
const validateSportConditionBySport = (
	sports: FreeBetSportConditionForValidation[],
	ticket: TicketForValidation,
	betType: number | undefined
): string[] => {
	const sportCondition = sports.find(sport => sport.SportType === ticket.SportType);
	if (!sportCondition) return ['SPORT'];

	const disableMessages: string[] = [];

	if (!sportCondition.AllLeagues && (ticket.LeagueId == null || !sportCondition.LeagueIds.includes(ticket.LeagueId))) {
		disableMessages.push('LEAGUE');
	}

	if (!isSportBetTypeAllowed(sportCondition, betType)) {
		disableMessages.push('BET_TYPE');
	}

	return disableMessages;
};

const validateFreeBetOddsByCondition = (
	condition: FreeBetVoucherConditionForValidation,
	ticket: TicketForValidation,
	baseOddsType: number
): FreeBetConditionValidationResult => {
	const finalOddsType = checkFreeBetValidationOddsType(ticket, baseOddsType);
	const result = validateOddsWithRules(
		Number(ticket.DisplayOdds),
		finalOddsType,
		condition.Condition.OddsLimitRule || [],
		ticket.Bettype
	);

	if (!result.isValid && result.violationType && result.violationValue !== null) {

		return {
			isEnabled: false,
			disableMessages: [result.violationType],
			disableMessageData: {
				...(result.violationType === 'ODDS_MIN' && { minOdds: result.violationValue }),
				...(result.violationType === 'ODDS_MAX' && { maxOdds: result.violationValue }),
				oddsType: result.effectiveOddsType
			}
		};
	}

	return { isEnabled: true, disableMessages: null };
};

// 確認最終bettype的odds type
const checkFreeBetValidationOddsType = (
	ticket: TicketForValidation | undefined,
	baseOddsType: number
): number => {
	if (!ticket) return baseOddsType;

	const hasTicketOddsType = ticket.OddsType != null && ticket.OddsType > 0;
	const ticketOddsType = hasTicketOddsType ? ticket.OddsType as number : undefined;
	const isDecOddsType =
		ticket.PriceType === 1 ||
		(Boolean(ticket.isSBR) && (ticket.Bettype === '5' || ticket.Bettype === '15'));

	if (isDecOddsType) return WebOddsType.DEC;

	if (ticketOddsType != null && ticketOddsType !== baseOddsType) {
		return ticketOddsType;
	}

	return ticketOddsType || baseOddsType;
};

const validateOddsWithRules = (
	displayOdds: number,
	oddsType: number,
	OddsLimitRule: FreeBetOddsLimitRule[] | null,
	bettype?: string | number
): {
	isValid: boolean;
	violationType: 'ODDS_MIN' | 'ODDS_MAX' | null;
	violationValue: number | null;
	effectiveOddsType: number;
} => {
	const { comparableOdds, effectiveOddsType } = convertNegativeOdds(displayOdds, oddsType);
	const targetRule = OddsLimitRule?.find(rule => rule.OddsType === effectiveOddsType);

	if (!targetRule) {
		return { isValid: true, violationType: null, violationValue: null, effectiveOddsType };
	}

	if (targetRule.MinOdds != null && comparableOdds < targetRule.MinOdds) {
		return {
			isValid: false,
			violationType: 'ODDS_MIN',
			violationValue: targetRule.MinOdds,
			effectiveOddsType
		};
	}

	if (targetRule.MaxOdds != null && comparableOdds > targetRule.MaxOdds) {
		return {
			isValid: false,
			violationType: 'ODDS_MAX',
			violationValue: targetRule.MaxOdds,
			effectiveOddsType
		};
	}

	return { isValid: true, violationType: null, violationValue: null, effectiveOddsType };
};


export const checkMinMaxBet = (
	quota: number,
	minMaxBetLimits: MinMaxBetLimitsType | undefined,
	t: (key: string) => string
): MinMaxBetCheckResult => {
	if (!minMaxBetLimits) {
		return { isDisabledByMinMaxBet: false, disableMessage: null };
	}
	const { minBet, maxBet } = minMaxBetLimits;
	if (quota > maxBet) {
		return {
			isDisabledByMinMaxBet: true,
			disableMessage: t('lbl_FBHint06'),
		};
	}
	if (quota < minBet) {
		return {
			isDisabledByMinMaxBet: true,
			disableMessage: t('lbl_FBHint07'),
		};
	}
	return { isDisabledByMinMaxBet: false, disableMessage: null };
};

const FreebetCondition = ['SPORT', 'LEAGUE', 'BET_TYPE'] as const;
type FreebetConditionType = typeof FreebetCondition[number];

const FreebetMinMaxOddsCondition = ['ODDS_MIN', 'ODDS_MAX'] as const;
type FreebetMinMaxOddsConditionType = typeof FreebetMinMaxOddsCondition[number];

export const getOddsTypeLabel = (oddsType: number, t: TranslateGeneral): string => {
	return oddsType === WebOddsType.HK
		? t('China_Odds')
		: t(`lbl_OddsTypeMin${oddsType}`);
};

export const formatFreeBetDisableMessage = (
	voucher: {
		disableMessages?: string[];
		disableMessageData?: {
			minOdds?: number;
			maxOdds?: number;
			oddsType?: number;
		};
	},
	t: TranslateGeneral,
	isTooltip: boolean = false,
): string => {
	const { disableMessageData, disableMessages } = voucher;

	if (disableMessages != null && disableMessages.length > 0) {
		
		const hasBasicConditionError = disableMessages.some(code => FreebetCondition.includes(code as FreebetConditionType));
		const hasOddsConditionError = disableMessages.filter(code => FreebetMinMaxOddsCondition.includes(code as FreebetMinMaxOddsConditionType));

		if (hasBasicConditionError) {
			const conditionTexts: string[] = [];
			
			if (disableMessages.includes('SPORT')) {
				conditionTexts.push(t('lbl_FBHintConditionsD01'));
			}
			if (disableMessages.includes('LEAGUE')) {
				conditionTexts.push(t('lbl_FBHintConditionsD02'));
			}
			if (disableMessages.includes('BET_TYPE')) {
				conditionTexts.push(t('lbl_FBHintConditionsD03'));
			}

			if (conditionTexts.length > 0) {
				const combinedConditions = conditionTexts.join(', ');
				return t.raw('lbl_FBHintConditions01').replace('{conditions}', combinedConditions);
			}
		}

		if (hasOddsConditionError.length > 0 && disableMessageData?.oddsType != null) {
			const oddsTypeLabel = getOddsTypeLabel(disableMessageData.oddsType, t);
			
			if (hasOddsConditionError.includes('ODDS_MIN') && disableMessageData.minOdds != null) {
				const resourceKey = isTooltip ? 'lbl_FBHint03' : 'lbl_FBHint04';
				return t.raw(resourceKey)
					.replace('{Dynamic_Odds}', `${disableMessageData.minOdds}`)
					.replace('{Odds_Format}', oddsTypeLabel);
			}
			
			if (hasOddsConditionError.includes('ODDS_MAX') && disableMessageData.maxOdds != null) {
				const resourceKey = isTooltip ? 'lbl_FBHint08' : 'lbl_FBHint09';
				return t.raw(resourceKey)
					.replace('{Dynamic_Odds}', `${disableMessageData.maxOdds}`)
					.replace('{Odds_Format}', oddsTypeLabel);
			}
		}
	}
	return '';
};

const convertNegativeOdds = (
	displayOdds: number,
	oddsType: number
): { comparableOdds: number; effectiveOddsType: number } => {

	if (oddsType === WebOddsType.MY && displayOdds < 0) {
		return {
			comparableOdds: hkOdds(displayOdds),
			effectiveOddsType: WebOddsType.HK
		};
	}
	
	// ID 負賠率 to MY Odds (odds same as HK Odds)
	if (oddsType === WebOddsType.INDO && displayOdds < 0) {
		return {
			comparableOdds: indoOddsToMyOdds(displayOdds),
			effectiveOddsType: WebOddsType.HK
		};
	}

	// US 負賠率 to MY Odds (odds same as HK Odds)
	if (oddsType === WebOddsType.US && displayOdds < 0) {
		return {
			comparableOdds: usOddsToMyOdds(displayOdds),
			effectiveOddsType: WebOddsType.HK
		};
	}
	
	return {
		comparableOdds: displayOdds,
		effectiveOddsType: oddsType
	};
};

export type UseFreeBetBetLimitsValidationResult = {
	checkBetLimits: (quota: number) => MinMaxBetCheckResult;
};

export const blockBetForInvalidFreeBetOdds = (
	oddsType: number,
): boolean => {
	const store = useBetslipStore.getState();

	if (store.useVoucher !== 'FreeBet') return false;

	const ticket = store.ticketCarts['normal'].multiTickets[0];
	const condition = store.selectedFreeBetCondition;
	
	if (!ticket || !condition) return false;

	const validation = validateFreeBetCondition(condition, ticket, oddsType);

	if (!validation.isEnabled) {
		store.removeFreeBetForInvalidOdds();
		return true; 
	}

	return false; 
};

export const useFreeBetBetLimitsValidation = (): UseFreeBetBetLimitsValidationResult => {
	const t = useTranslations('General');
	const [singleTicket] = useBetslipStore(state => [
		state.ticketCarts['normal'].multiTickets[0],
	]);

	const minMaxBetLimits = useMemo((): MinMaxBetLimitsType | undefined => {
		if (!singleTicket) return undefined;
		return {
			minBet: singleTicket.minBet,
			maxBet: singleTicket.maxBet,
		};
	}, [singleTicket]);

	const checkBetLimits = useCallback(
		(quota: number): MinMaxBetCheckResult =>
			checkMinMaxBet(quota, minMaxBetLimits, t),
		[minMaxBetLimits, t]
	);

	return {
		checkBetLimits,
	};
};
